import React from "react"
import Table from "../common/Table"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody } from "material-ui"
import ColumnSelector from "../../components/common/columnSelector/ColumnSelector"
import strings from "../../localization/strings"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import { IDeviceDocument, IDeviceReportColumns, ITableColumnSelect } from "../../models"
import * as timeHelper from "../../helpers/timeHelper"
import { red500, grey100, grey300 } from "material-ui/styles/colors"
import AccountStore from "../../stores/AccountStore"
import DevicePermissionsDetailCol from "./DevicePermissionsDetailCol"
import moment from "moment"
import { checkJobFieldAvailable } from "../../helpers/jobHelper"

interface IProps {
	devices: IDeviceDocument[]
	total: number
	page: number
	limit: number
	limitOptions: number[]
	selectPermission: boolean
	editPermission: boolean
	selectedDevices: IDeviceDocument[]
	deviceColumns: IDeviceReportColumns
	handleSelect(selectedDevices: IDeviceDocument[]): void
	onRowClick(device: IDeviceDocument): void
	onPageClick(page: number): void
	onLimitChange(limit: number): void

	tableColumnSelect?: ITableColumnSelect[]
	handleChangeTableColumns?(columns: ITableColumnSelect[]): void
}
interface IState {
	hoveredRowNumber: number
}

export default class DeviceTable extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		this.state = {
			hoveredRowNumber: null
		}
	}

	render() {
		const showCheckBox = this.props.selectPermission ? true : false
		return (
			<div style={{ position: "relative" }}>
				<Table
					items={this.props.devices}
					selectedItems={this.props.selectedDevices}
					allowClickRow={this.props.editPermission}
					handleClickRow={this.props.onRowClick}
					handleSelect={this.props.handleSelect}
					total={this.props.total}
					limit={this.props.limit}
					limitOptions={this.props.limitOptions}
					offset={this.props.page}
					onLimitChange={this.props.onLimitChange}
					onPageClick={this.props.onPageClick}
					onRowHover={hoveredRowNumber => this.setState({ hoveredRowNumber })}
					onRowHoverExit={rowNumber => this.setState({ hoveredRowNumber: null })}
				>
					<TableHeader displaySelectAll={showCheckBox} adjustForCheckbox={showCheckBox}>
						<TableRow className="ShortSelectBoxTableHeader">
							{this.props.deviceColumns.IncludeName &&
								<TableHeaderColumn className="TextOverflow" title={strings.Name} style={styles.columnHeader}>{strings.Name}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeHomeHierarchy &&
								<TableHeaderColumn className="TextOverflow" title={strings.Home_Hierarchy} style={styles.columnHeader}>{strings.Home_Hierarchy}</TableHeaderColumn>
							}
							{AccountStore.getJobsEnabled() &&
								this.props.deviceColumns.IncludeHomeJob &&
								<TableHeaderColumn className="TextOverflow" title={checkJobFieldAvailable("singular") ? `Home ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Home_Job} style={styles.columnHeader}>{checkJobFieldAvailable("singular") ? `Home ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Home_Job}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeConnected &&
								<TableHeaderColumn className="TextOverflow" title={strings.Connected} style={styles.columnHeader}>{strings.Connected}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeType &&
								<TableHeaderColumn className="TextOverflow" title={strings.Type} style={{ ...styles.columnHeader, width: 35 }}>{strings.Type}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludePhoneNumber && this.props.deviceColumns.IncludeCode &&
								<TableHeaderColumn className="TextOverflow" title={strings.Phone_Number_and_Code} style={styles.columnHeader}>{strings.Phone_Number_and_Code}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludePhoneNumber && !this.props.deviceColumns.IncludeCode &&
								<TableHeaderColumn className="TextOverflow" title={strings.Phone_Number} style={styles.columnHeader}>{strings.Phone_Number}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeCode && !this.props.deviceColumns.IncludePhoneNumber &&
								<TableHeaderColumn className="TextOverflow" title={strings.Code} style={styles.columnHeader}>{strings.Code}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeMode &&
								<TableHeaderColumn className="TextOverflow" title={strings.Mode} style={styles.columnHeader}>{strings.Mode}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeOS &&
								<TableHeaderColumn className="TextOverflow" title={strings.os} style={{ ...styles.columnHeader, width: 50 }}>{strings.os}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeDescription &&
								<TableHeaderColumn className="TextOverflow" title={strings.Description} style={styles.columnHeader}>{strings.Description}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeAppVersion &&
								<TableHeaderColumn className="TextOverflow" title={strings.App_Version} style={styles.columnHeader}>{strings.App_Version}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeActive &&
								<TableHeaderColumn className="TextOverflow" title={strings.Active} style={styles.columnHeader}>{strings.Active}</TableHeaderColumn>
							}
							{this.props.deviceColumns.IncludeDevicePermissions &&
								<TableHeaderColumn className="TextOverflow" title={strings.Permissions} style={{ paddingLeft: 5, paddingRight: 5, textAlign: "center" }}>{strings.Permissions}</TableHeaderColumn>
							}
							{this.props.tableColumnSelect &&
								<TableHeaderColumn style={{ ...styles.columnHeader, width: 50, float: Object.keys(this.props.deviceColumns).filter(x => this.props.deviceColumns[x] === true).length === 0 ? "right" : "none" }}>
									<ColumnSelector
										columns={this.props.tableColumnSelect}
										handleChange={this.props.handleChangeTableColumns}
									/>
								</TableHeaderColumn>
							}
						</TableRow>
					</TableHeader>
					<TableBody displayRowCheckbox={showCheckBox} showRowHover={true} deselectOnClickaway={false}>
						{this.props.devices.map((device, index) => {
							const hierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(device.HierarchyItemId)
							const lastConnectedTime = device.CurrentStatus &&
								moment().isAfter(device.CurrentStatus.StatusTime) &&
								timeHelper.getPassedDateTime(device.CurrentStatus.StatusTime)

							const isIVRDevice = Array.isArray(device.ApprovedIVRPhoneNumbers) && device.ApprovedIVRPhoneNumbers.length > 0
							let type = null
							if (isIVRDevice) {
								type = 'Phone'
							} else if (device.CurrentStatus && device.CurrentStatus.WiFi) {
								type = 'WiFi'
							} else if (device.CurrentStatus && device.CurrentStatus.Cellular) {
								type = 'Cellular'
							}
							const mode = isIVRDevice ? 'IVR' :
								(device.UserId ? strings.Individual_Mode :
									device.IsMobileClock ? strings.MobileClockMode :
										strings.Clock_Mode)
							const os = !isIVRDevice && device.CurrentStatus &&
								(device.CurrentStatus.OS == 0 ? "iOS" :
									device.CurrentStatus.OS == 1 ? "Android" : "")
							const description = !isIVRDevice && device.CurrentStatus && device.CurrentStatus.Description
							const finddVersion = !isIVRDevice && device.CurrentStatus && device.CurrentStatus.AppVersion

							const phoneCode = isIVRDevice ? device.ApprovedIVRPhoneNumbers[0] : device.RegistrationCode
							return (
								<TableRow data-intercom-target={index === 0 && "menu-Devices-openDeviceDetails"} className="ShortSelectBoxTableRow" key={index} selected={this.props.selectedDevices.map(i => i.Id).indexOf(device.Id) > -1}>
									{this.props.deviceColumns.IncludeName &&
										<TableRowColumn style={styles.columnValue} title={device.Name}>{device.Name}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeHomeHierarchy &&
										<TableRowColumn style={styles.columnValue}>
											<span
												title={hierarchyName.includes(strings.Hierarchy_Removed) ? strings.Hierarchy_Removed : hierarchyHelper.getFullPathLabel(device.HierarchyItemId)}
												style={{ color: hierarchyName.includes(strings.Hierarchy_Removed) && red500 }}
											>
												{hierarchyName.includes(strings.Hierarchy_Removed) ? strings.Hierarchy_Removed : hierarchyName}
											</span>
										</TableRowColumn>
									}
									{AccountStore.getJobsEnabled() &&
										this.props.deviceColumns.IncludeHomeJob &&
										<TableRowColumn style={styles.columnValue} title={device.JobName}>{device.JobName}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeConnected &&
										<TableRowColumn style={styles.columnValue} title={lastConnectedTime}>{lastConnectedTime}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeType &&
										<TableRowColumn style={{ ...styles.columnValue, width: 35 }} title={type}>{type}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeCode &&
										<TableRowColumn 
											style={styles.columnValue} 
											title={phoneCode}>
											{phoneCode}
										</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeMode &&
										<TableRowColumn style={styles.columnValue} title={mode}>{mode}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeOS &&
										<TableRowColumn style={{ ...styles.columnValue, width: 50 }} title={os}>{os}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeDescription &&
										<TableRowColumn style={styles.columnValue} title={description}>{description}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeAppVersion &&
										<TableRowColumn style={styles.columnValue} title={finddVersion}>{finddVersion}</TableRowColumn>
									}
									{this.props.deviceColumns.IncludeActive &&
										<DeviceTableActiveCol
											hovered={this.state.hoveredRowNumber === index}
											selected={this.props.selectedDevices.find(i => i.Id === device.Id) ? true : false}
											device={device}
										/>
									}
									{this.props.deviceColumns.IncludeDevicePermissions &&
										<TableRowColumn style={styles.columnValue}>
											{!isIVRDevice &&
												<DevicePermissionsDetailCol
													deviceId={device.Id}
													devicePermissions={device.CurrentStatus ? device.CurrentStatus.DevicePermissions : null}
												/>
											}
										</TableRowColumn>
									}
									{this.props.tableColumnSelect &&
										<TableRowColumn style={{ ...styles.columnValue, width: 50, float: Object.keys(this.props.deviceColumns).filter(x => this.props.deviceColumns[x] === true).length === 0 ? "right" : "none" }}></TableRowColumn>
									}
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</div>
		)
	}
}

const styles = {
	columnValue: {
		textAlign: "left",
		paddingLeft: 5,
		paddingRight: 5
	},
	columnHeader: {
		textAlign: "left",
		paddingLeft: 5,
		paddingRight: 5
	}
}

interface IDeviceTableActiveColProps {
	selected: boolean
	hovered: boolean
	device: IDeviceDocument
}
interface IDeviceTableActiveColState {
	activationDate: string
}
class DeviceTableActiveCol extends React.Component<IDeviceTableActiveColProps, IDeviceTableActiveColState> {
	constructor(props: IDeviceTableActiveColProps) {
		super(props)
		this.state = {
			activationDate: ""
		}
	}

	async componentDidMount() {
		if (this.props.device.ActivationDate) {
			const clientLocalTime = await timeHelper.getDateTime(this.props.device.HierarchyItemId, this.props.device.JobId, this.props.device.ActivationDate)
			this.setState({ activationDate: clientLocalTime.format("MMM. DD, YYYY h:mm A") })
		}
	}

	render() {
		return (
			<TableRowColumn
				style={{
					...styles.columnValue,
					backgroundColor:
						this.props.hovered ?
							grey100 :
							this.props.selected ?
								grey300 :
								null
				}}
				title={this.state.activationDate}
			>
				{this.state.activationDate}
			</TableRowColumn>
		)
	}
}